/**
 * M1InfoScreen.tsx
 * Last updated: 2025-08-11 15:50:19
 * Updated by: JulianPolly
 */

import React, { useState, useRef, useEffect } from "react";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import KopfImg from "./assets/Kopf.png";

// --- Helper function for glossary alias resolution ---
function resolveGlossarEntry(
    glossar: Record<string, string | { alias: string }>,
    key: string
): { key: string; text: string } | undefined {
    const seen = new Set<string>();
    let current = key;
    while (current && !seen.has(current)) {
        seen.add(current);
        const entry = glossar[current];
        if (typeof entry === "string") return { key: current, text: entry };
        if (entry && typeof entry === "object" && "alias" in entry) {
            current = entry.alias;
        } else {
            break;
        }
    }
    return undefined;
}

// v9 Task Icon
const TaskIcon = (
    <svg width="44" height="44" style={{marginRight: 16, verticalAlign: 'middle'}} fill="none" viewBox="0 0 24 24">
        <rect x="3" y="4" width="18" height="16" rx="2" fill="#e7f1fa" />
        <rect x="6.5" y="7.5" width="11" height="1.5" rx="0.75" fill="#3b82f6" />
        <rect x="6.5" y="11" width="7" height="1.5" rx="0.75" fill="#3b82f6" />
        <circle cx="17" cy="16" r="1" fill="#3b82f6" />
    </svg>
);

// Tooltip for glossary terms (now alias-safe!)
const BegriffTooltip: React.FC<{
    begriff: string;
    children: React.ReactNode;
    glossar: Record<string, string | { alias: string }>;
    language: string;
}> = ({ begriff, children, glossar, language }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!visible) return;
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setVisible(false);
            }
        };
        window.addEventListener("mousedown", onClick);
        return () => window.removeEventListener("mousedown", onClick);
    }, [visible]);

    const resolved = resolveGlossarEntry(glossar, begriff);

    // Update aria-label based on language
    const ariaLabel = language === 'de'
        ? `Begriffserklärung für ${begriff}`
        : `Term explanation for ${begriff}`;

    return (
        <span
            ref={ref}
            style={{
                position: "relative",
                cursor: "pointer",
                background: "#e7f1fa",
                color: "#2563eb",
                borderRadius: 5,
                padding: "0 4px",
                transition: "background 0.2s",
                fontWeight: 500,
                zIndex: 10,
                display: "inline-block"
            }}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            onClick={e => {
                setVisible((v: boolean) => !v);
                e.stopPropagation();
            }}
            tabIndex={0}
            aria-label={ariaLabel}
        >
            <>
                {children}
                {visible && (
                    <span
                        style={{
                            position: "absolute",
                            left: "50%",
                            bottom: "100%",
                            transform: "translate(-50%, 12px)",
                            minWidth: 400,
                            maxWidth: 700,
                            background: "#fff",
                            color: "#222",
                            border: "1.5px solid #bcd3f6",
                            borderRadius: 14,
                            boxShadow: "0 10px 38px 0 rgba(0,0,0,0.17)",
                            zIndex: 1050,
                            padding: "28px 32px",
                            fontWeight: 400,
                            fontSize: 17,
                            lineHeight: 1.6,
                            pointerEvents: "auto",
                            whiteSpace: "pre-wrap",
                            marginTop: 12
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <b>{resolved?.key ?? begriff}</b>
                        <br />
                        {resolved?.text ?? begriff}
                    </span>
                )}
            </>
        </span>
    );
};

// Highlight and explain terms only at first occurrence
function parseParagraphs(
    paragraphs: string[],
    glossar: Record<string, string | { alias: string }>,
    language: string
) {
    if (!Array.isArray(paragraphs)) {
        console.error("Expected paragraphs to be an array but got:", paragraphs);
        return [<p key="error">Error loading content</p>];
    }

    const terms = Object.keys(glossar).sort((a, b) => b.length - a.length);
    const used = new Set<string>();
    const parsed: React.ReactNode[] = [];

    paragraphs.forEach((text, pIdx) => {
        const re = new RegExp(`\\b(${terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\b`, "g");
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        let match: RegExpExecArray | null;
        let i = 0;

        while ((match = re.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(text.substring(lastIndex, match.index));
            }
            const begriff = match[1];
            if (!used.has(begriff)) {
                used.add(begriff);
                parts.push(
                    <BegriffTooltip
                        begriff={begriff}
                        key={begriff + "-" + pIdx + "-" + i}
                        glossar={glossar}
                        language={language}
                    >
                        {begriff}
                    </BegriffTooltip>
                );
            } else {
                parts.push(begriff);
            }
            lastIndex = match.index + begriff.length;
            i++;
        }
        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }
        parsed.push(
            <p key={pIdx} style={{marginTop: 0, marginBottom: 18, textAlign: "justify", textJustify: "inter-word"}}>
                {parts}
            </p>
        );
    });
    return parsed;
}

export const M1InfoScreenComponent: React.FC = () => {
    // Add the language hook
    const { t, language } = useLanguage();

    // Get the glossary terms from translations
    const glossar = t('glossary', 'terms', { returnObjects: true }) as Record<string, string | { alias: string }>;

    // Get translations for the content
    const title = t('experiments', 'M1InfoScreen', 'title') || "M1: Wie Entscheidungen beeinflusst werden";
    const imageAlt = t('experiments', 'M1InfoScreen', 'imageAlt') || "Kopf";
    const paragraphsTranslated = t('experiments', 'M1InfoScreen', 'paragraphs', { returnObjects: true }) as string[];
    const task = t('experiments', 'M1InfoScreen', 'task') || "<b>Aufgabe 1:</b> Erkläre mit Hilfe von Material M1 in eigenen Worten, was man unter dem Begriff <b>Bias</b> versteht und wie Biases unsere Entscheidungsfindung beeinflussen können.";

    // Default paragraphs as fallback
    const defaultParagraphs = [
        "Entscheidungen sind komplex: Viele Faktoren beeinflussen, wie Menschen entscheiden – beispielsweise Gefühle wie Wut oder Freude, Zeitdruck, Geldmangel oder persönliche Einstellungen (z. B. Umweltbewusstsein).",
        "Bereits in den 1960er-Jahren haben die Psychologen Daniel Kahneman und Amos Tversky gezeigt: Unser Denken funktioniert in zwei Systemen. System I denkt schnell, automatisch und emotional – es trifft Entscheidungen aus dem Bauch heraus und spart Energie. System II denkt langsam, überlegt und logisch – es wird aktiv, wenn wir bewusst nachdenken oder schwierige Entscheidungen treffen müssen.",
        "Im Alltag nutzen wir meistens System I, weil es weniger anstrengend ist. Das spart Energie, führt aber manchmal auch zu Fehlern. Denn wir verwenden sogenannte Heuristiken und Biases: Denkabkürzungen und Denkfehler, die Entscheidungen erleichtern, aber nicht immer richtig sind.",
        "Heuristiken sind einfache Faustregeln, mit denen wir schnell entscheiden können. Sie helfen uns oft, können aber auch zu Fehleinschätzungen führen – zum Beispiel: Wenn der Himmel grau ist, denken viele, dass es bald regnet. Diese Annahme stimmt häufig, aber nicht immer.",
        "Menschen nutzen Heuristiken, weil unser Denken begrenzt ist: Wir haben oft keine Zeit oder Kraft, alle Informationen genau zu prüfen. Deshalb treffen wir schnelle, manchmal suboptimale Entscheidungen. Im Beispiel: Wer den Regenschirm mitnimmt, obwohl es gar nicht regnet, hat vielleicht unnötig entschieden. Das Problem: Heuristiken vereinfachen unsere Welt und Entscheidungen, gehen dabei aber manchmal zu weit.",
        "Biases (kognitive Verzerrungen) sind systematische Denkfehler, die unsere Wahrnehmung und Bewertung einer Situation verzerren. Wir nehmen die Situation nicht vollständig wahr, sondern achten nur auf bestimmte Aspekte und treffen so manchmal ungünstige Entscheidungen – meist, ohne es zu merken."
    ];

    // Use translations if available, otherwise use defaults
    const paragraphs = Array.isArray(paragraphsTranslated) && paragraphsTranslated.length > 0
        ? paragraphsTranslated
        : defaultParagraphs;

    return (
        <div style={{
            maxWidth: 1250,
            margin: "0 auto",
            background: "#fff",
            borderRadius: 14,
            boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
            padding: "44px 64px"
        }}>
            <h2 style={{ marginBottom: 22 }}>{title}</h2>
            <div style={{
                fontSize: 17,
                lineHeight: 1.7,
                textAlign: "justify",
                textJustify: "inter-word",
                position: "relative",
                minHeight: 260
            }}>
                <img
                    src={KopfImg}
                    alt={imageAlt}
                    style={{
                        float: "right",
                        marginLeft: 44,
                        marginBottom: 18,
                        width: 260,
                        maxWidth: "44%",
                        borderRadius: 16,
                        boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)",
                        objectFit: "cover"
                    }}
                />
                {parseParagraphs(paragraphs, glossar, language)}
            </div>
            <div style={{
                background: "#f8f9fa",
                borderRadius: 8,
                padding: "22px 24px",
                marginTop: 36,
                display: 'flex',
                alignItems: 'center'
            }}>
                {TaskIcon}
                <div dangerouslySetInnerHTML={{ __html: task }}>
                </div>
            </div>
        </div>
    );
};

export const M1InfoScreen: ScreenConfig = {
    component: M1InfoScreenComponent,
    defaultResult: {},
    canContinueDefault: true
};